import { CommunicationBody } from "@/components/communications/CommunicationBody.tsx";
import {
  FromDisplay,
  RecipientsSection,
} from "@/components/communications/TextMessageParticipants.tsx";
import { EntityAgentComposer } from "@/components/messages/Omnibox/EntityAgentComposer.tsx";
import { ParticipantPill } from "@/components/ui/participant-pill.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import type { EmailDraftParticipant } from "@/types/artifacts.ts";
import type { CommunicationDetail as CommunicationDetailData } from "@/types/communications.ts";
import { toUrnString } from "@/types/urn.ts";
import type { ReactNode } from "react";

const AttachmentList = ({
  attachments,
}: {
  attachments: CommunicationDetailData["attachments"];
}) => {
  if (attachments.length === 0) return null;
  return (
    <div className="px-4 py-3 border-t">
      <p className="text-xs text-gray-500 mb-1">Attachments</p>
      <ul className="text-sm list-disc pl-5">
        {attachments.map((a) => (
          <li key={a.fileId}>
            {a.downloadPath ? (
              <a
                href={resolveApiPath(a.downloadPath)}
                className="text-primary underline underline-offset-2"
                data-statsig-name="communication-attachment-download"
              >
                {a.fileName}
              </a>
            ) : (
              a.fileName
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// External (non-domain) senders/recipients get the amber highlight, mirroring
// the schema-driven email card (EmailRecipientsBlock).
const EmailPill = ({ participant }: { participant: EmailDraftParticipant }) => {
  const isExternal = !participant.isDomainHandle;
  return (
    <ParticipantPill
      participant={participant}
      variant="email"
      showAvatar
      showParticipantStatus={false}
      isExternal={isExternal}
      className={cn(isExternal && "bg-amber-100 border-amber-300", "w-fit")}
    />
  );
};

const EmailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-row items-start gap-2 text-sm">
    <div className="font-medium w-12 shrink-0 pt-1 text-muted-foreground">{label}</div>
    <div className="flex flex-row flex-wrap gap-1 items-center flex-1 min-w-0">{children}</div>
  </div>
);

/** Detail-view timestamp: always shows the full date and the time. */
const formatReceivedAt = (epochMs: number): string =>
  new Date(epochMs).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

/**
 * Card footer with the received date + time, and — where the host asked for it —
 * the composer that hands this message to the agent.
 */
const ReceivedFooter = ({
  receivedAt,
  agentComposer,
}: {
  receivedAt: number;
  agentComposer: ReactNode;
}) => (
  <>
    <Separator />
    <div className="flex flex-col gap-2.5 px-4 py-2.5">
      <p className="text-xs text-muted-foreground">Received {formatReceivedAt(receivedAt)}</p>
      {agentComposer}
    </div>
  </>
);

/**
 * Rendering of a communication source (email or text/SMS). Shared by the
 * full-page view (`CommunicationDetail`) and the right-sidebar view
 * (`CommunicationSidebar`).
 *
 * `showAgentComposer` is opt-in rather than the default because
 * `CommunicationThreadSidebar` renders one of these per message in a
 * conversation — defaulting it on would stack a Reply button under every
 * message in the thread.
 */
export const CommunicationContent = ({
  data,
  showAgentComposer = false,
}: {
  data: CommunicationDetailData;
  /** Offer "Reply with AI" under the message. See the note above. */
  showAgentComposer?: boolean;
}) => {
  const agentComposer = showAgentComposer ? (
    <EntityAgentComposer
      urn={toUrnString({ type: "source", id: data.sourceId })}
      label="Reply with AI"
      placeholder={data.channel === "text" ? "Reply to this message…" : "Reply to this email…"}
      statsigName="communication-reply-with-ai"
    />
  ) : null;

  if (data.channel === "email") {
    const body = data.body.trim().length > 0 ? data.body : data.plainTextBody;
    return (
      <div className="flex flex-col">
        <div className="flex flex-col gap-3 px-4 py-3.5">
          <EmailRow label="From:">
            {data.from ? (
              <EmailPill participant={data.from} />
            ) : (
              <span className="text-sm text-muted-foreground italic">(unknown sender)</span>
            )}
          </EmailRow>
          {data.to.length > 0 && (
            <EmailRow label="To:">
              {data.to.map((p) => (
                <EmailPill key={p.id} participant={p} />
              ))}
            </EmailRow>
          )}
          {data.cc.length > 0 && (
            <EmailRow label="Cc:">
              {data.cc.map((p) => (
                <EmailPill key={p.id} participant={p} />
              ))}
            </EmailRow>
          )}
          {data.subject && <h3 className="font-semibold text-base break-words">{data.subject}</h3>}
        </div>
        <Separator />
        <div className="px-4 py-3">
          {body.trim().length > 0 ? (
            <CommunicationBody body={body} />
          ) : (
            <div className="text-sm text-muted-foreground italic">(No content available)</div>
          )}
        </div>
        <AttachmentList attachments={data.attachments} />
        <ReceivedFooter receivedAt={data.receivedAt} agentComposer={agentComposer} />
      </div>
    );
  }

  // Text/SMS layout — reuses the read-only sender/recipient components from the
  // text message draft artifact (FromToPanel shape), minus the editor/char-count.
  return (
    <div className="flex flex-col">
      <div className="px-4 pt-3.5 flex flex-col gap-4">
        {data.from ? (
          <FromDisplay from={data.from} />
        ) : (
          <div className="text-sm text-gray-500">From: (unknown sender)</div>
        )}
        {data.to.length > 0 && <RecipientsSection recipients={data.to} />}
      </div>
      <div className="flex flex-col gap-2 px-4 py-3.5">
        <Separator className="my-2" />
        <p className="text-sm whitespace-pre-wrap break-words">{data.body}</p>
      </div>
      <AttachmentList attachments={data.attachments} />
      <ReceivedFooter receivedAt={data.receivedAt} agentComposer={agentComposer} />
    </div>
  );
};
